import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  formatPercent,
  toNumber,
  clamp
} from './panel-utils.js';

const DEFAULT_ITEMS = [
  {
    id: 'msg-1',
    source: 'HN',
    author: 'dataforge',
    sentiment: 0.62,
    score: 184,
    timestamp: Date.now() - 1000 * 60 * 60 * 4,
    text: 'Strong take. The throughput gains are real if you pre-compute the dependency map.'
  },
  {
    id: 'msg-2',
    source: 'Reddit',
    author: 'flux_agent',
    sentiment: -0.2,
    score: 92,
    timestamp: Date.now() - 1000 * 60 * 60 * 3.2,
    text: 'This looks cool, but the abstraction layers will slow down power users.'
  },
  {
    id: 'msg-3',
    source: 'X',
    author: '@neon_ops',
    sentiment: 0.12,
    score: 56,
    timestamp: Date.now() - 1000 * 60 * 60 * 2.4,
    text: 'Great UI. Needs a faster way to jump between threads though.'
  },
  {
    id: 'msg-4',
    source: 'YouTube',
    author: 'signal_lab',
    sentiment: 0.84,
    score: 210,
    timestamp: Date.now() - 1000 * 60 * 60 * 1.1,
    text: 'The graph navigation is slick. Would love a bulk comment moderation tool.'
  }
];

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Conversation Timeline');
  root.classList.add('ew-timeline-panel');
  body.classList.add('ew-timeline-body');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const filterBar = document.createElement('div');
  filterBar.className = 'ew-timeline-filters';

  const timeline = document.createElement('div');
  timeline.className = 'ew-timeline';

  body.append(metrics, filterBar, timeline);
  let currentNode = node;

  const update = (next) => {
    currentNode = next;
    titleEl.textContent = resolveTitle(next, 'Conversation Timeline');
    const items = normalizeItems(next);
    const selected = next?.data?.selected || null;
    const filters = next?.data?.filters || {};
    const activeSources = Array.isArray(filters.sources) && filters.sources.length
      ? new Set(filters.sources)
      : null;
    const filtered = activeSources
      ? items.filter((item) => activeSources.has(item.source))
      : items;
    const groupByThread = next?.data?.groupByThread ?? next?.props?.groupByThread ?? true;

    metaEl.textContent = next?.data?.window || next?.props?.window || 'Recent';

    const avgSentiment = filtered.length
      ? filtered.reduce((acc, item) => acc + item.sentiment, 0) / filtered.length
      : 0;
    const pos = filtered.filter((item) => item.sentiment > 0.1).length;
    const neg = filtered.filter((item) => item.sentiment < -0.1).length;

    metrics.innerHTML = '';
    metrics.append(
      createMetric('messages', formatNumber(filtered.length)),
      createMetric('positive', formatNumber(pos)),
      createMetric('negative', formatNumber(neg)),
      createMetric('avg', formatPercent((avgSentiment + 1) / 2, 0))
    );

    renderFilters(filterBar, items, activeSources, filters, api, currentNode);

    timeline.innerHTML = '';
    const threads = groupByThread ? groupThreads(filtered) : [{ id: 'all', title: null, items: filtered }];
    threads.forEach((thread) => {
      const wrap = document.createElement('div');
      wrap.className = 'ew-thread';

      if (thread.title) {
        const header = document.createElement('div');
        header.className = 'ew-thread-header';
        const title = document.createElement('span');
        title.className = 'ew-thread-title';
        title.textContent = thread.title;
        const meta = document.createElement('span');
        meta.className = 'ew-thread-meta';
        meta.textContent = `${thread.items.length} replies`;
        header.append(title, meta);
        wrap.appendChild(header);
      }

      thread.items.forEach((item) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'ew-timeline-item';
        if (selected && selected === item.id) row.classList.add('is-active');
        if (item.status) row.classList.add(`is-${item.status}`);

        const marker = document.createElement('span');
        marker.className = `ew-timeline-marker ${sentimentClass(item.sentiment)}`;
        marker.title = sentimentLabel(item.sentiment);

        const content = document.createElement('div');
        content.className = 'ew-timeline-content';
        content.style.setProperty('--ew-depth', item.depth || 0);

        const header = document.createElement('div');
        header.className = 'ew-timeline-head';
        const who = document.createElement('span');
        who.className = 'ew-timeline-author';
        who.textContent = item.author || 'unknown';
        const meta = document.createElement('span');
        meta.className = 'ew-timeline-meta';
        meta.textContent = `${item.source} ú ${formatTimestamp(item.timestamp)}`;
        header.append(who, meta);

        const text = document.createElement('div');
        text.className = 'ew-timeline-text';
        text.textContent = item.text || '';

        const footer = document.createElement('div');
        footer.className = 'ew-timeline-footer';
        if (item.score !== null && item.score !== undefined) {
          const score = document.createElement('span');
          score.className = 'ew-tag';
          score.textContent = `score ${formatNumber(item.score)}`;
          footer.appendChild(score);
        }

        const actions = document.createElement('div');
        actions.className = 'ew-timeline-actions';
        ['approve', 'hide', 'flag'].forEach((action) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `ew-btn-mini ew-timeline-action ew-action-${action}`;
          btn.textContent = action;
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const status = action === 'approve' ? 'approved' : action === 'hide' ? 'hidden' : 'flagged';
            updateItem(api, currentNode, item.id, { status });
            api.emit('node:input', { id: node.id, field: 'moderation', value: status, itemId: item.id });
          });
          actions.appendChild(btn);
        });

        content.append(header, text, footer, actions);
        row.append(marker, content);
        row.addEventListener('click', () => {
          api.setData({ selected: item.id });
          api.emit('node:input', { id: node.id, field: 'comment', value: item.id });
        });
        wrap.appendChild(row);
      });

      timeline.appendChild(wrap);
    });
  };

  update(node);
  return { el: root, update };
}

function normalizeItems(node) {
  const raw = node?.data?.items || node?.props?.items;
  const list = Array.isArray(raw) && raw.length ? raw : DEFAULT_ITEMS;
  return list.map((item, idx) => ({
    id: String(item.id ?? `comment-${idx}`),
    source: item.source || 'Feed',
    author: item.author || 'anonymous',
    sentiment: clamp(toNumber(item.sentiment, 0), -1, 1),
    score: item.score ?? null,
    timestamp: item.timestamp ?? item.time ?? Date.now() - idx * 3600 * 1000,
    text: item.text || '',
    threadId: item.threadId || item.thread || item.id || `thread-${idx}`,
    parentId: item.parentId || null,
    status: item.status || null,
    depth: 0
  }));
}

function sentimentClass(value) {
  if (value >= 0.35) return 'is-positive';
  if (value <= -0.35) return 'is-negative';
  return 'is-neutral';
}

function sentimentLabel(value) {
  if (value >= 0.35) return 'Positive';
  if (value <= -0.35) return 'Negative';
  return 'Neutral';
}

function formatTimestamp(ts) {
  const time = typeof ts === 'string' ? Date.parse(ts) : ts;
  if (!Number.isFinite(time)) return 'unknown time';
  return new Date(time).toLocaleString();
}

function renderFilters(root, items, activeSources, filters, api, node) {
  root.innerHTML = '';
  const sources = Array.from(new Set(items.map((item) => item.source))).filter(Boolean);
  if (!sources.length) return;

  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.className = 'ew-chip';
  if (!activeSources || activeSources.size === 0) allBtn.classList.add('is-active');
  allBtn.textContent = 'All';
  allBtn.addEventListener('click', () => {
    api.setData({ filters: { ...(filters || {}), sources: [] } });
    api.emit('node:input', { id: node.id, field: 'filter', value: { sources: [] } });
  });
  root.appendChild(allBtn);

  sources.forEach((source) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ew-chip';
    if (activeSources?.has(source)) btn.classList.add('is-active');
    btn.textContent = source;
    btn.addEventListener('click', () => {
      const next = new Set(activeSources || []);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      api.setData({ filters: { ...(filters || {}), sources: Array.from(next) } });
      api.emit('node:input', { id: node.id, field: 'filter', value: { sources: Array.from(next) } });
    });
    root.appendChild(btn);
  });
}

function groupThreads(items) {
  const map = new Map();
  items.forEach((item) => {
    const threadId = item.threadId || item.id;
    if (!map.has(threadId)) map.set(threadId, []);
    map.get(threadId).push(item);
  });

  const threads = Array.from(map.entries()).map(([id, list]) => {
    const sorted = [...list].sort((a, b) => a.timestamp - b.timestamp);
    const depths = computeDepths(sorted);
    const enriched = sorted.map((item) => ({ ...item, depth: depths.get(item.id) || 0 }));
    const latest = sorted[sorted.length - 1];
    const title = latest?.threadTitle || latest?.title || latest?.text?.slice(0, 48);
    return { id, title, items: enriched, latest: latest?.timestamp || 0 };
  });

  return threads.sort((a, b) => b.latest - a.latest);
}

function computeDepths(items) {
  const map = new Map();
  const byId = new Map(items.map((item) => [item.id, item]));
  const calcDepth = (item, seen = new Set()) => {
    if (!item?.parentId) return 0;
    if (seen.has(item.id)) return 0;
    const parent = byId.get(item.parentId);
    if (!parent) return 1;
    seen.add(item.id);
    return 1 + calcDepth(parent, seen);
  };
  items.forEach((item) => {
    map.set(item.id, clamp(calcDepth(item), 0, 4));
  });
  return map;
}

function updateItem(api, node, itemId, patch) {
  const raw = node?.data?.items;
  const source = Array.isArray(raw) && raw.length ? raw : normalizeItems(node);
  const next = source.map((item) => (item.id === itemId ? { ...item, ...patch } : item));
  api.setData({ items: next });
}
